import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import WorkspaceLayout from "@/components/WorkspaceLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, XCircle, MessageSquare, FilePlus2, UploadCloud } from "lucide-react";

const TABS = [
  { v: "all", l: "All" },
  { v: "Unclaimed", l: "Unclaimed" },
  { v: "Claim Requested", l: "Pending Claims" },
  { v: "Linked", l: "Linked" },
  { v: "Alumni", l: "Alumni" },
];

export default function StudentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "claims" ? "Claim Requested" : (searchParams.get("tab") || "all");
  const [tab, setTab] = useState(initialTab);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ all: 0, unclaimed: 0, claim_requested: 0, linked: 0, alumni: 0 });

  const load = async (t = tab) => {
    const status_filter = t === "all" ? null : t;
    const { data } = await api.get("/records/students", { params: { status_filter } });
    setRows(data.rows);
    setCounts(data.counts);
  };
  useEffect(() => { load(tab); }, [tab]);

  const onTab = (v) => {
    setTab(v);
    if (v === "Claim Requested") setSearchParams({ tab: "claims" });
    else if (v === "all") setSearchParams({});
    else setSearchParams({ tab: v });
  };

  const decide = async (id, decision, note) => {
    try {
      await api.post(`/claims/student/${id}/decision`, { decision, note });
      toast.success(`Claim ${decision}d`);
      load(tab);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const countFor = (v) => {
    if (v === "all") return counts.all;
    if (v === "Unclaimed") return counts.unclaimed;
    if (v === "Claim Requested") return counts.claim_requested;
    if (v === "Linked") return counts.linked;
    if (v === "Alumni") return counts.alumni;
    return 0;
  };

  return (
    <WorkspaceLayout
      subtitle="Institution workspace"
      title="Student directory"
      actions={
        <div className="flex gap-2">
          <Link to="/workspace/students/bulk"><Button variant="outline" className="rounded-sm gap-2" data-testid="students-bulk-link"><UploadCloud className="w-4 h-4" />Bulk Upload</Button></Link>
          <Link to="/workspace/students/add"><Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="students-add-link"><FilePlus2 className="w-4 h-4" />Add Student</Button></Link>
        </div>
      }
    >
      <div className="flex flex-wrap gap-px bg-ec-line border border-ec-line mb-6" data-testid="students-tabs">
        {TABS.map((t) => {
          const active = tab === t.v;
          return (
            <button
              key={t.v}
              onClick={() => onTab(t.v)}
              className={`flex-1 min-w-[120px] px-4 py-3 text-left bg-white transition-colors ${active ? "bg-ec-emerald/5" : "hover:bg-ec-mute"}`}
              data-testid={`students-tab-${t.v.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="eyebrow">{t.l}</div>
              <div className={`font-display text-2xl font-black mt-1 ${active ? "text-ec-emerald" : ""}`}>{countFor(t.v)}</div>
            </button>
          );
        })}
      </div>

      <Card className="rounded-sm border-ec-line">
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="text-center py-16 text-ec-ink-soft" data-testid="students-empty">No records in this view.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-ec-mute">
                <tr className="text-left">
                  <Th>Name</Th>
                  <Th>Student ID</Th>
                  <Th>Course</Th>
                  <Th>Batch</Th>
                  <Th>PAN</Th>
                  <Th>Status</Th>
                  <Th>EthicCred</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-ec-line hover:bg-ec-mute/50" data-testid={`student-row-${r.student_id}`}>
                    <Td>
                      <div className="font-semibold">{r.student_name}</div>
                      <div className="text-xs text-ec-ink-soft">{r.personal_email}</div>
                    </Td>
                    <Td className="font-mono-plex">{r.student_id}</Td>
                    <Td>{r.course}<div className="text-xs text-ec-ink-soft">{r.department}</div></Td>
                    <Td>{r.batch}<div className="text-xs text-ec-ink-soft">{r.admission_year}–{r.graduation_year}</div></Td>
                    <Td className="font-mono-plex text-xs" data-testid={`stu-pan-${r.student_id}`}>{r.pan_masked || "—"}</Td>
                    <Td><StatusBadge status={r.status} extra={r.student_status} /></Td>
                    <Td className="font-mono-plex text-xs">{r.linked_ethic_id || "—"}</Td>
                    <Td className="text-right">
                      {r.status === "Claim Requested" && (
                        <div className="flex justify-end gap-1.5">
                          <Button size="sm" className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 gap-1 h-7" onClick={() => decide(r.id, "approve")} data-testid={`stu-approve-${r.id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-sm border-ec-error text-ec-error gap-1 h-7" onClick={() => {
                            const note = prompt("Reason for rejection (optional):") || "";
                            decide(r.id, "reject", note);
                          }} data-testid={`stu-reject-${r.id}`}>
                            <XCircle className="w-3 h-3" /> Reject
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-sm gap-1 h-7" onClick={() => {
                            const note = prompt("Clarification question:") || "";
                            if (note) decide(r.id, "clarify", note);
                          }} data-testid={`stu-clarify-${r.id}`}>
                            <MessageSquare className="w-3 h-3" /> Clarify
                          </Button>
                        </div>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </WorkspaceLayout>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 eyebrow ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
}
function StatusBadge({ status, extra }) {
  const map = {
    "Unclaimed": "bg-ec-ink-soft/10 text-ec-ink-soft border-ec-ink-soft/30",
    "Claim Requested": "bg-amber-500/10 text-amber-700 border-amber-500/40",
    "Linked": "bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30",
    "Disputed": "bg-ec-error/10 text-ec-error border-ec-error/30",
    "Alumni": "bg-ec-ink-soft/10 text-ec-ink-soft border-ec-ink-soft/30",
  };
  return (
    <div className="flex flex-col gap-1">
      <Badge className={`rounded-sm border ${map[status] || ""}`}>{status}</Badge>
      {extra && extra !== "Active" && <span className="text-[10px] font-mono-plex text-ec-ink-soft">{extra}</span>}
    </div>
  );
}
